import { LogError } from "../../utils/ErrorTelemetry";
import { LogEvents } from "../../utils/LogEvents";
import { ErrorStringFromException, isEmptyString } from "../../utils/utils";
import { CreateDocumentStorage } from "../factory/DocumentStorageFactory";
import { DurationMeasureType } from "../interfaces/ILogEvent";
import { IStoredTranscription, TranscriptionStorageType } from "../interfaces/IStoredTranscription";
import { IStoredTranscriptionArray } from "../interfaces/IStoredTranscriptionArray";
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { ITranscription, ITranscriptionManager } from "../interfaces/ITranscriptionManager";
import { DocumentStorage } from "../office-js/DocumentStorage";
import { HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import { DocumentStorageKeys } from "../office-js/telemetry/DocumentStorageKeys";
import { GetStorageMigrationState, StorageMigrationHandler, StorageMigrationStatus, storageMigrationTaskPromise, UpdateStorageMigrationState } from "./StorageMigrationHandler";

 export abstract class TranscriptionDocumentStorageManager implements ITranscriptionManager {

  protected documentStorage: DocumentStorage;
  protected documentStorageTypeFromStoredTranscription: TranscriptionStorageType | null = null;

  constructor(documentStorage: DocumentStorage) {
    this.documentStorage = documentStorage;
  }

  abstract GetTranscription(storageIndex?: number | undefined): ITranscription;

  abstract UpdateTranscriptionSegment(segmentIndex: number, segmentText: string, speakerName: string | null, updateAll: boolean, storageIndex?: number | undefined): Promise<void>;

  abstract GetStorageVersion(): number;

  abstract GetStoredTranscription(storageIndex?: number | undefined): IStoredTranscription;

  abstract DeleteTranscription(storageIndex?: number | undefined): void;

  abstract IsMultiTranscriptStorageSupported(): boolean;

  abstract WriteTranscriptionToStorage(storedTranscription: IStoredTranscription): void;

  async HandleStorageMigration(): Promise<StorageMigrationStatus> {
    if (this.documentStorage.SupportedDocumentStorageType != this.documentStorageTypeFromStoredTranscription) {
      const storageMigrationHandler: StorageMigrationHandler = new StorageMigrationHandler(this.documentStorage, this.documentStorageTypeFromStoredTranscription!);
      const storageMigrationStatus = await storageMigrationHandler.HandleStorageMigration(JSON.stringify(this.GetStoredTranscription()));
      if (storageMigrationStatus == StorageMigrationStatus.MigrationFinished) {
        this.documentStorageTypeFromStoredTranscription = this.documentStorage.SupportedDocumentStorageType!;
      }
      return storageMigrationStatus;
    } else {
      return StorageMigrationStatus.MigrationNotRequired;
    }
  }

  protected async WriteStoredTranscription(storedTranscription: IStoredTranscription | IStoredTranscriptionArray): Promise<void> {
    LogEvents.DocumentStorage.Start();
    LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.WriteStoredTranscriptionDuration);

    try {
        if (GetStorageMigrationState() == StorageMigrationStatus.MigrationInitiated) {
          await storageMigrationTaskPromise;
        }
        const storedTranscriptionStr = JSON.stringify(storedTranscription);
        if (isEmptyString(storedTranscriptionStr)) {
          throw new Error("Stored Transcription can't be undefined or empty.");
        }
        await this.documentStorage.WriteTranscribeContentToClient(storedTranscriptionStr);
        HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, true);
        HostAppSettings.Set(HostAppSettingName.StoredTranscription, storedTranscription);

        /* If Storage Migration was initiated but not completed, try cleaning from old storage and
        mark migraiton as Finished. */
        if (GetStorageMigrationState() == StorageMigrationStatus.MigrationWriteToNewStorageFailed ||
        GetStorageMigrationState() == StorageMigrationStatus.MigrationDeleteFromOldStorageFailed) {
          await this.DeleteTranscribeContentFromOldStorage();
        }

        LogEvents.DocumentStorage.End();
    } catch (e) {
        LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
        throw e;
    }
}

  protected async DeleteStoredTranscription(): Promise<void> {
    LogEvents.DocumentStorage.Start();
    LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.DeleteStoredTranscriptionDuration);

    try {
        if (GetStorageMigrationState() == StorageMigrationStatus.MigrationInitiated) {
          await storageMigrationTaskPromise;
        }
        await this.documentStorage.DeleteTranscribeContentFromClient();
        HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, false);
        HostAppSettings.Set(HostAppSettingName.StoredTranscription, null);

        /* If Storage Migration was initiated but not completed, try cleaning from old storage and
        mark migraiton as Finished. */
        if (GetStorageMigrationState() == StorageMigrationStatus.MigrationWriteToNewStorageFailed ||
        GetStorageMigrationState() == StorageMigrationStatus.MigrationDeleteFromOldStorageFailed) {
          await this.DeleteTranscribeContentFromOldStorage();
        }
        LogEvents.DocumentStorage.End();
    } catch (e) {
        LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
        throw e;
    }
}

  private async DeleteTranscribeContentFromOldStorage() {
    let oldDocumentStorage: DocumentStorage;
    try {
      oldDocumentStorage = CreateDocumentStorage(this.documentStorageTypeFromStoredTranscription!);
      await oldDocumentStorage.DeleteTranscribeContentFromClient();

      // Mark Migration as finished.
      UpdateStorageMigrationState(StorageMigrationStatus.MigrationFinished);
      this.documentStorageTypeFromStoredTranscription = this.documentStorage.SupportedDocumentStorageType!;
    } catch(e) {
      LogError(
        LogEventNames.Warning,
        'Transcription content delete operation from old storage (' +
          oldDocumentStorage!.SupportedDocumentStorageType +
          ') failed with error ' +
          ErrorStringFromException(e)
      );
    }

  }
 }